import { Link } from "react-router-dom";

const SECTIONS = [
  {
    id: "intro",
    title: "Introduction",
    body: [
      "The Daily Find (\"we,\" \"us,\" \"our\") operates an AI-driven affiliate content automation platform. This Privacy Policy explains what information we collect, how we use it, who we share it with, and the rights you have over your data.",
      "By using the service you agree to this policy. If you do not agree, please stop using the service.",
    ],
  },
  {
    id: "data-we-collect",
    title: "Data we collect",
    body: [
      "Account data: email address, hashed password, profile preferences (display name, niche tags, storefront handle).",
      "Connected-platform data: when you authorize a TikTok, TikTok Shop, AliExpress, Amazon Associates, or Clickbank account we store the access tokens, refresh tokens, scope grants, and account identifiers required to call those APIs on your behalf.",
      "Content you create: product entries you import, affiliate links you generate, video scripts, captions, generated MP4 files, slideshow images, voiceover MP3s, and any uploads.",
      "Inbound email: when you forward or receive email to your `*@thedailyfind.app` inbox addresses we store the sender, recipient, subject, body, headers, attachments metadata, and AI-derived classification.",
      "Usage analytics: timestamps, click counts on your affiliate short-links (including IP address, user-agent and referrer of the clicker), quota usage against third-party APIs, and aggregated cost telemetry.",
      "Cookies: a single session cookie holds your authentication token. We do not load third-party analytics, ad pixels, or marketing trackers on legal pages (`/privacy`, `/terms`) or public storefront pages.",
    ],
  },
  {
    id: "how-we-use",
    title: "How we use it",
    body: [
      "Run the core service — render videos, schedule posts, route inbox classifications, generate AI script drafts, and serve your public storefront.",
      "Enforce the FROZEN compliance brief — every AI-drafted reply or script passes through a banned-terms / disclosure / niche-specific lint before it can be sent or rendered.",
      "Operate, debug, and improve the platform. Server logs are retained for 30 days for diagnostics and then rotated.",
      "Send transactional email (login confirmations, sale notifications) from `noreply@thedailyfind.app`. We do not send marketing email without a separate opt-in.",
    ],
  },
  {
    id: "third-parties",
    title: "Third parties we share data with",
    body: [
      "OpenAI (via the Emergent universal LLM key) — for script generation, classification, and product extraction.",
      "Pexels — public stock-video API queried with non-personal keywords from your scripts.",
      "HeyGen — when you opt into avatar videos, the script text and avatar/voice IDs are sent for synthesis.",
      "Resend — outbound email delivery and inbound email parsing on `thedailyfind.app`.",
      "RapidAPI (TikTok provider) — public trending hashtag, sound, and video discovery only; no personal data is sent.",
      "TikTok, TikTok Shop, AliExpress, Amazon Associates, Clickbank — platform APIs called using YOUR connected-account tokens, scoped to the permissions you granted.",
      "We do not sell your data and we do not transfer it to advertisers.",
    ],
  },
  {
    id: "retention",
    title: "Data retention",
    body: [
      "Account, products, and storefront data: kept until you delete the account.",
      "Rendered video files and audio: kept until you delete them or until 180 days of inactivity, whichever comes first.",
      "Inbound emails: kept until you delete them. Marketing-spam classified messages are auto-archived after 7 days; archived messages are deleted 90 days later.",
      "Short-link click events: kept indefinitely in aggregated form; raw IP addresses are truncated to /24 after 30 days.",
      "On account deletion all the above plus connected-platform tokens are erased within 7 days.",
    ],
  },
  {
    id: "your-rights",
    title: "Your rights (GDPR / CCPA style)",
    body: [
      "Access — request a JSON export of everything we hold about you.",
      "Correction — edit or update your profile and content directly in the app, or email us.",
      "Deletion — delete your account from Settings or email us. We will erase your data within 7 days.",
      "Portability — your generated videos, products, and links are all downloadable.",
      "Object / Restrict — disconnect any third-party integration at any time from Settings; this revokes the token immediately.",
      "Complaints — you may lodge a complaint with your local data protection authority. We will cooperate.",
    ],
  },
  {
    id: "cookies",
    title: "Cookies",
    body: [
      "We set a single first-party session cookie (`ap_session`) containing a signed JWT that authenticates you to the API. It expires after 14 days of inactivity.",
      "We do not use third-party cookies, advertising pixels, or cross-site trackers.",
    ],
  },
  {
    id: "minors",
    title: "Children's privacy",
    body: [
      "The service is intended for users 18 and older. We do not knowingly collect data from children under 13. If you believe a child has provided us data, contact us and we will erase it.",
    ],
  },
  {
    id: "security",
    title: "Security",
    body: [
      "Passwords are hashed with bcrypt. Third-party access tokens are stored encrypted at rest. All traffic to the service is HTTPS. We use unique-handle indexes and per-user data scoping to prevent cross-tenant leakage.",
      "No system is impenetrable. If you suspect a breach contact `security@thedailyfind.app` immediately.",
    ],
  },
  {
    id: "changes",
    title: "Changes to this policy",
    body: [
      "We may update this policy from time to time. Material changes will be announced in-app at least 14 days before they take effect.",
    ],
  },
  {
    id: "contact",
    title: "Contact",
    body: [
      "Questions, deletion requests, or complaints: privacy@thedailyfind.app",
      "Operator: The Daily Find (sole proprietor, USA).",
    ],
  },
];

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <article className="max-w-3xl mx-auto px-5 sm:px-8 py-12">
        <Link to="/" className="text-xs mono text-emerald-400 hover:text-emerald-300"
              data-testid="privacy-back-home">← thedailyfind.app</Link>
        <h1 className="text-3xl sm:text-4xl font-bold mt-6 mb-2 tracking-tight"
            data-testid="privacy-title">Privacy Policy</h1>
        <div className="text-xs mono text-zinc-500">Last updated: 23 February 2026</div>

        <div className="mt-6 rounded-md border border-amber-900/60 bg-amber-950/30 px-4 py-3 text-xs text-amber-200">
          This is a starting template for an AI-driven affiliate marketing tool.
          Have a lawyer review it for your jurisdiction before relying on it for compliance.
        </div>

        <div className="mt-8 space-y-7" data-testid="privacy-sections">
          {SECTIONS.map((s) => (
            <section key={s.id} id={s.id}>
              <h2 className="text-base font-semibold text-zinc-100 mb-2">{s.title}</h2>
              <div className="space-y-2 text-sm text-zinc-300 leading-relaxed">
                {s.body.map((p, i) => <p key={i}>{p}</p>)}
              </div>
            </section>
          ))}
        </div>

        <footer className="mt-12 pt-6 border-t border-zinc-800 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-2">
          <span className="mono">© The Daily Find</span>
          <span className="flex gap-4">
            <Link to="/terms" className="hover:text-zinc-300" data-testid="privacy-footer-terms">Terms</Link>
            <a href="mailto:privacy@thedailyfind.app" className="hover:text-zinc-300">Contact</a>
          </span>
        </footer>
      </article>
    </div>
  );
}
